import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sparkyMessagesFor, leftoversFor, SPARKY_MAX, type CartItem } from '../lib/shopping';
import { recipes } from '../data/recipes';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older Safari / non-secure contexts: fall back to a hidden textarea.
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

export function CopyButton({ text, label, className = '' }: { text: string; label: string; className?: string }) {
  const [state, setState] = useState<'idle' | 'ok' | 'fail'>('idle');
  useEffect(() => {
    if (state === 'idle') return;
    const t = setTimeout(() => setState('idle'), 1800);
    return () => clearTimeout(t);
  }, [state]);
  return (
    <button
      type="button"
      onClick={async () => setState((await copyText(text)) ? 'ok' : 'fail')}
      className={`h-11 rounded-full px-5 font-sans text-[0.85rem] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink ${
        state === 'ok'
          ? 'bg-freeze text-paper'
          : state === 'fail'
            ? 'border border-goal text-goal'
            : 'bg-ink text-paper hover:opacity-90'
      } ${className}`}
    >
      {state === 'ok' ? 'Copied' : state === 'fail' ? 'Copy failed — select it' : label}
    </button>
  );
}

// The Walmart cart script: one card per Sparky message, each with its own
// copy button, in the order you paste them — then what the trip leaves in
// the fridge and how to not waste it.
export function ShoppingScript({ items, scaledLabel }: { items: CartItem[]; scaledLabel?: string | null }) {
  const msgs = sparkyMessagesFor(items);
  if (!msgs.length) return null;
  const all = msgs.join('\n\n');
  const left = leftoversFor(items, recipes);
  return (
    <div>
      <p className="mt-2 font-serif leading-relaxed text-ink-soft">
        Open the Walmart app, tap the smiley (Sparky) in the bottom bar, and paste these one at a time
        in order — wait for Sparky to confirm each batch before sending the next, it drops items if two
        land at once. Each one fits under Sparky’s paste limit. Everything from Liam’s freezer is already
        left off.{scaledLabel ? ` Amounts are for ${scaledLabel}.` : ''}
      </p>
      <ol className="mt-4 space-y-3">
        {msgs.map((m, i) => (
          <li key={i} className="rounded-xl border border-line bg-card p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="font-sans text-[0.72rem] font-bold uppercase tracking-[0.1em] text-ink-soft">
                Message {i + 1} of {msgs.length}
                <span className="ml-2 font-medium normal-case tracking-normal">
                  {m.length} / {SPARKY_MAX} chars
                </span>
              </span>
              <CopyButton text={m} label="Copy" />
            </div>
            <pre className="mt-3 select-all whitespace-pre-wrap break-words font-sans text-[0.88rem] leading-relaxed text-ink">
              {m}
            </pre>
          </li>
        ))}
      </ol>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <CopyButton text={all} label="Copy all as one message" className="border border-line !bg-card !text-ink" />
        <span className="font-sans text-[0.8rem] text-ink-soft">For ChatGPT, Instacart or any cart builder that takes a long paste.</span>
      </div>

      {left.length > 0 && (
        <section className="mt-6 rounded-xl border border-dashed border-line bg-paper/70 p-4" aria-label="What will be left over">
          <h3 className="font-sans text-[0.7rem] font-bold uppercase tracking-[0.1em] text-ink-soft">
            What this leaves in the fridge
          </h3>
          <p className="mt-1 font-serif text-[0.88rem] italic leading-snug text-ink-soft">
            Perishables where this uses well under a package. Shortest keeper first. Cook it, freeze it, or use it up.
          </p>
          <ul className="mt-3 space-y-3">
            {left.map((l) => (
              <li key={l.id} className="font-serif text-[0.95rem] leading-snug">
                <span className="font-medium">{l.name}</span>
                <span className="text-ink-soft">
                  {' '}— about {l.leftPct}% of a {l.pkg} left{l.keepsDays ? `, keeps ~${l.keepsDays} days` : ''}.
                </span>
                {l.alsoIn.length > 0 && (
                  <span className="text-ink-soft">
                    {' '}Cook it into{' '}
                    {l.alsoIn.map((r, i) => (
                      <span key={r.slug}>
                        {i > 0 && (i === l.alsoIn.length - 1 ? ' or ' : ', ')}
                        <Link to={`/recipe/${r.slug}`} className="underline decoration-dotted underline-offset-4 hover:decoration-solid">
                          {r.title}
                        </Link>
                      </span>
                    ))}
                    .
                  </span>
                )}
                {(l.freeze || l.quick?.length) && (
                  <ul className="mt-1 space-y-0.5 pl-4 font-sans text-[0.82rem] text-ink-soft">
                    {l.freeze && (
                      <li>
                        <span className="font-semibold text-freeze">Freeze: </span>
                        {l.freeze}
                      </li>
                    )}
                    {l.quick?.map((q) => (
                      <li key={q}>
                        <span className="font-semibold text-goal">10 min: </span>
                        {q}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
